import getConfig from 'next/config'

import {
  sharedStaticConfig,
  staticClientConfig,
} from '@ngb-frontend/shared/config'
import {
  getTestServerConfig,
  getTestClientConfig,
  getTestSharedConfig,
} from '@ngb-frontend/shared/test-utils'
import { isJsDom, string, truthy } from '@ngb-frontend/shared/utils'

import type {
  ClientConfig,
  ServerConfig,
  SharedConfig,
} from '@ngb-frontend/shared/types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSharedConfig = (pubConfig?: any): SharedConfig => {
  if (isJsDom()) return getTestSharedConfig()

  return {
    ...sharedStaticConfig,
    disableAuth: truthy(pubConfig?.disableAuth),
    disableEntraId: pubConfig?.environment === 'test',
    disableDateBlocking: truthy(pubConfig?.disableDateBlocking),
    mockApi: truthy(pubConfig?.mockApi),
    buildInfo: {
      productName: 'Next Generation Booking',
      commitSha: pubConfig?.commit,
      branch: pubConfig?.branch,
      timestamp: '',
    },
    features: {
      eligibilityCheck: truthy(pubConfig?.features?.eligibilityCheck),
      driverCourtesy: !truthy(pubConfig?.features?.disableCourtesy),
    },
    azure: {
      azureAD: {
        clientId: string(pubConfig?.azureAD?.clientId),
        authority: string(pubConfig?.azureAD?.authority),
      },
      azureEntraID: {
        clientId: string(pubConfig?.azureEntraID?.clientId),
        authority: string(pubConfig?.azureEntraID?.authority),
      },
      azureB2C: {
        clientId: string(pubConfig?.azureB2C?.clientId),
        authority: string(pubConfig?.azureB2C?.authority),
        scope: string(pubConfig?.azureB2C?.scope),
      },
    },
  }
}

export const getClientConfig = (): ClientConfig => {
  if (isJsDom()) return getTestClientConfig()

  const { publicRuntimeConfig: pubConfig } = getConfig()

  const { applicationId, ...ddSharedConfigs } = pubConfig?.dd || {}

  const { datadogLogConfig, datadogRumConfig, hotjarConfig } =
    staticClientConfig

  const sharedConfig = getSharedConfig(pubConfig)
  return {
    ...sharedConfig,
    google: {
      maps: pubConfig?.maps,
      tagManager: pubConfig?.tagManager,
    },
    onetrust: pubConfig?.onetrust,
    onetrustNFFleet: pubConfig?.onetrustNFFleet,
    datadogLogConfig: {
      ...datadogLogConfig,
      ...ddSharedConfigs,
    },
    datadogRumConfig: {
      ...datadogRumConfig,
      ...ddSharedConfigs,
      applicationId,
    },
    hotjarConfig: {
      ...hotjarConfig,
      version: hotjarConfig?.version || 6,
      id: pubConfig?.hotjarId,
    },
  }
}

export const getServerConfig = (): ServerConfig => {
  if (isJsDom()) return getTestServerConfig()

  const { publicRuntimeConfig: pubConfig } = getConfig()

  return {
    ...getSharedConfig(pubConfig),
    disableToken: truthy(process.env.DISABLE_TOKEN),
    sfBase: string(process.env.SF_BASE_URL),
    sfBaseALD: string(process.env.SF_BASE_URL_ALD),
    sitecoreBase: string(process.env.SITECORE_BASE),
    fleetopsContractManagementBase: string(
      process.env.FLEETOPS_CONTRACT_MANAGEMENT_BASE_URL,
    ),
    fleetopsLeaseObjectBase: string(process.env.FLEETOPS_LEASE_OBJECT_BASE_URL),
    apiTokenUrl: string(process.env.API_TOKEN_URL),
    apiTokenUrlALD: string(process.env.API_TOKEN_URL_ALD),
    tibco: {
      sfClientId: string(process.env.SF_CLIENT_ID),
      sfSecret: string(process.env.SF_SECRET),
      fleetopsContractManagementClientId: string(
        process.env.FLEETOPS_CONTRACT_MANAGEMENT_CLIENT_ID,
      ),
      fleetopsContractManagementSecret: string(
        process.env.FLEETOPS_CONTRACT_MANAGEMENT_SECRET,
      ),
      fleetopsLeaseObjectClientId: string(
        process.env.FLEETOPS_LEASE_OBJECT_CLIENT_ID,
      ),
      fleetopsLeaseObjectSecret: string(
        process.env.FLEETOPS_LEASE_OBJECT_SECRET,
      ),
    },
    aldAuth: {
      sfClientId: string(process.env.ALD_SF_CLIENT_ID),
      sfSecret: string(process.env.ALD_SF_SECRET),
      sfUser: string(process.env.ALD_SF_USERNAME),
      sfPassword: string(process.env.ALD_SF_PASSWORD),
    },
    denodo: {
      base: string(process.env.DENODO_BASE),
      username: string(process.env.DENODO_USERNAME),
      password: string(process.env.DENODO_PASSWORD),
    },
    ipInfoToken: string(process.env.IPINFO_TOKEN),
    smartling: {
      projectId: string(process.env.SMARTLING_PROJECT_ID),
      userId: string(process.env.SMARTLING_ID),
      secret: string(process.env.SMARTLING_SECRET),
      gitToken: string(process.env.SMARTLING_BOT_TOKEN),
    },
  }
}
